const dobSeparators = ['-', '.', '/'];
// Cache values for current locale
// Must be unset on init
let dobSeparator;
let dateFormat;

/**
 * All supported date separators
 */
export function getDateSeparators() {
    return dobSeparators;
}

/**
 * Returns date separator for the specified format
 * Eg: Returns "-" for DD-MM-YYYY, "/" dor DD/MM/YYYY
 * @param  {String} dobFormat Date format as specified by griffin
 * @return {String}           Date separator
 */
export function getDOBSeparator(dobFormat) {
    // If we have cached the dobSeparator, return it
    if (dobSeparator) {
	   return dobSeparator;
    }

    let separator = '';
    getDateSeparators().forEach(function (ele, iter) {
        if (dobFormat.indexOf(ele) !== -1) { // eslint-disable-line consumerweb/no-indexof
            separator = ele;
        }
    });
    if (separator) {
        dobSeparator = separator;
    }
    return separator;
}

/**
 * Formats the date based on the specified pattern
 * @param  {String} dob        DOB to format
 * @param  {String} dobPattern Date Pattern as specified by griffin
 * @return {String}            Formatted DOB
 */
export function getFormattedDob(dob, dobPattern) {
    let dobFormat = getDateFormat(dobPattern);
    let dateSeparator = getDOBSeparator(dobPattern);
    let dateDigitsOnly = dob.replace(/\D+/g, '').slice(0, 8);
    let formattedDate = '';

    // Did user manually enter the separator?
    let lastCharIsSeparator = false;
    if (dob[dob.length - 1] === dateSeparator) {
        lastCharIsSeparator = true;
    }

    if (dobFormat === 'DMY' || dobFormat === 'MDY') {
        // If '/' is the dateSeparator,
        // Format as [2digits]/[2digits]/[4digits]
        // Eg: DD/MM/YYYY or MM/DD/YYYY

        let dd = dateDigitsOnly.slice(0, 2);
        let mm = dateDigitsOnly.slice(2, 4);
        let yy = dateDigitsOnly.slice(4);

        // Theres probably a better way to do this
        if (dd) {
            formattedDate = dd;
            if (dateDigitsOnly.length > 2 || (dateDigitsOnly.length === 2 && lastCharIsSeparator)) {
                formattedDate += dateSeparator;
            }
        }
        if (mm) {
            formattedDate += mm;
            if (dateDigitsOnly.length > 4 || (dateDigitsOnly.length === 4 && lastCharIsSeparator)) {
                formattedDate += dateSeparator;
            }
        }
        if (yy) {
            formattedDate += yy;
        }

    } else {
        // Format as YYYY/DD/MM
        let yy = dateDigitsOnly.slice(0, 4);
        let mm = dateDigitsOnly.slice(4, 6);
        let dd = dateDigitsOnly.slice(6);
        if (yy) {
            formattedDate = yy;
            if (dateDigitsOnly.length > 4 || (dateDigitsOnly.length === 4 && lastCharIsSeparator)) {
                formattedDate += dateSeparator;
            }
        }
        if (mm) {
            formattedDate += mm;
            if (dateDigitsOnly.length > 6 || (dateDigitsOnly.length === 6 && lastCharIsSeparator)) {
                formattedDate += dateSeparator;
            }
        }
        if (dd) {
            formattedDate += dd;
        }
    }

    return formattedDate;
}

export function isOfLegalAge(dob, dobPattern) {
    let dateObject = parseDob(dob, dobPattern);
    if (isDateStringValid(dateObject)) {
        let { year, month, day } = dateObject;
        let birthDate = new Date(year, month - 1, day);
        // user should be of exact 18 years of age or older
        let tempDate = new Date(birthDate.getFullYear() + 18, birthDate.getMonth(), birthDate.getDate());

        return tempDate <= new Date().setHours(0, 0, 0, 0);
    }

    return false;
}

/**
 * Validates date of birth from DoB string.
 *
 * @param {string} date of birth
 * @return {boolean} is valid
 */
export function validateDob(dob, dobPattern) {
    let dateObject = parseDob(dob, dobPattern);
    if (isDateStringValid(dateObject)) {
        let { year, month, day } = dateObject;
    return isDateValid(year, month - 1, day);
    }

    return false;
}

function isDateValid(year, month, day) {
    // dates like 02/31 33/33 should return false
    let date = new Date(year, month, day);

    let fullYear = date.getFullYear();
    let dateMonth = date.getMonth();
    let dateDay = date.getDate();

    // If birth year is in the future or before 1900, dob is invalid
    let now = new Date();
    if (fullYear > now.getFullYear() || fullYear < 1900) {
        return false;
    }

    if (fullYear === parseInt(year, 10) && dateMonth === parseInt(month, 10) && dateDay === parseInt(day, 10)) {
        return true;
    }
    return false;
}

/**
 * Parses the date string to return an object in the following format
 * {
 * 	day,
 * 	month,
 * 	year
 * }
 * Eg: If input is "11/12/2017", returns
 * {
 * 	day: 11,
 * 	month: 12,
 * 	year: 2017
 * }
 */
export function parseDob(inputVal, dobPattern) {
    let day;
    let month;
    let year;
    let sep = getDOBSeparator(dobPattern);
    let partsArr = inputVal.split(sep);
    let first = partsArr[0];
    let sec = partsArr[1];
    let third = partsArr[2];

    if (getDateFormat(dobPattern) === 'DMY') {
        day = first;
        month = sec;
        year = third;
    } else if (getDateFormat(dobPattern) === 'YMD') {
        year = first;
        month = sec;
        day = third;
    } else {
        month = first;
        day = sec;
        year = third;
    }

    return {
        day,
        month,
        year
    };

}

/**
 * Checks whether the date string is a valid date
 */
export function isDateStringValid({ day, month, year }) {
    // Validate only on entering the last digit of the dob
    let dayLen = ('' + day).length;
    let monLen = ('' + month).length;
    let yrLen = ('' + year).length;
    let validDay = dayLen === 1 || dayLen === 2;
    let validMon = monLen === 1 || monLen === 2;
    let validYr = yrLen === 4;
    return validDay && validMon && validYr;

}

/**
 * Returns the date format as a 3 letter string
 * Returns
 * 	1. 'DMY' for DDMMYYYY format
 * 	2. 'MDY' for MMDDYYYY format
 * 	3. 'YMD' for YYYYMMDD format
 * @param  {String} dobFormat Format of Date of Birth
 * @return {String}           Date Format
 */
export function getDateFormat(dobFormat) {
    // cache date format value
    if (dateFormat) {
        return dateFormat;
    }

    let dateFormatComponents = dobFormat.split(getDOBSeparator(dobFormat));

    // Its can be 'D' or 'DD'
    let dIdx = dateFormatComponents.indexOf('D') !== -1 ? dateFormatComponents.indexOf('D') : dateFormatComponents.indexOf('DD'), // eslint-disable-line consumerweb/no-indexof
    mIdx = dateFormatComponents.indexOf('MM'); // eslint-disable-line consumerweb/no-indexof

    let formatComponentMap = getFormatComponentIdxMappings();
    if (formatComponentMap.DMY.day === dIdx && formatComponentMap.DMY.month === mIdx) {
        dateFormat = 'DMY';
        return dateFormat;
    } else if (formatComponentMap.MDY.day === dIdx && formatComponentMap.MDY.month === mIdx) {
        dateFormat = 'MDY';
        return dateFormat;
    }

    // Neither DMY nor MDY
    dateFormat = 'YMD';
    return dateFormat;
}

function getFormatComponentIdxMappings() {
    return {
        DMY: {
            day: 0,
            month: 1,
            year: 2
        },
        MDY: {
            day: 1,
            month: 0,
            year: 2
        },
        YMD: {
            day: 2,
            month: 1,
            year: 0
        }
    };
}

export function isValidDate(year, month, day) {
    // dates like 02/31 33/33 should return false
    let date = new Date(year, month, day);

    let fullYear = date.getFullYear();
    let dateMonth = date.getMonth();
    let dateDay = date.getDate();

    // If birth year is in the future or before 1900, dob is invalid
    let now = new Date();
    if (fullYear > now.getFullYear() || fullYear < 1900) {
        return false;
    }

    if (fullYear === parseInt(year, 10) && dateMonth === parseInt(month, 10) && dateDay === parseInt(day, 10)) {
        return true;
    }
    return false;
}

/**
 * Resets the cached variables. Required mostly for unit tests
 */
export function reset() {
    dobSeparator = null;
    dateFormat = null;
}
